/**
 * 落地页模板的单一事实来源（admin / worker / landing 共用）。
 * 新增模板时只改这里：worker 用 TEMPLATE_IDS 做校验，admin 用 TEMPLATES 渲染表单，
 * landing 的组件注册表用 TemplateId 做键。把「是否用 appName/logo/buttonColor、默认色、
 * 标签」当数据，而不是散在各处的 if 分支。
 */
export const TEMPLATE_IDS = ['shield', 'playstore', 'download', 'appstore', 'custom'] as const;

export type TemplateId = (typeof TEMPLATE_IDS)[number];

/** Link-level Download copy. Empty strings inherit the visitor's localized defaults. */
export interface ButtonCopy {
  buttonText: string;
  buttonSubtitle: string;
}

/**
 * 运行时兜底默认模板:link 配置缺失 / 上游 /config 返回 link=null 时用它。
 * 不能用 'shield'(它 disabled:true,已停用)。这是「默认模板」的单一事实来源——
 * monorepo 内的兜底都该 import 它;无法依赖 shared 的边界处(landing 的 Pages
 * Function `_middleware.ts`、db `schema.ts` 的列默认)各留一份本地副本,改这里时同步。
 */
export const DEFAULT_TEMPLATE: TemplateId = 'download';

export interface TemplateDescriptor {
  id: TemplateId;
  /** admin 下拉的完整标签 */
  label: string;
  /** 列表 Badge 用的短标签 */
  shortLabel: string;
  /** 是否使用 appName + logo 字段 */
  hasAppName: boolean;
  /** 是否使用 buttonColor 字段 */
  hasButtonColor: boolean;
  /** buttonColor 留空时的默认色（仅 hasButtonColor 为 true 时有意义） */
  defaultButtonColor?: string;
  /** appName 输入框标签的后缀（如 Play Store 标题） */
  appNameHint?: string;
  /** 暂时停用：仍可被 templateById 解析（现有链接 Badge/渲染正常），但不在 admin 新建下拉中出现 */
  disabled?: boolean;
  /**
   * logo 字段语义为「背景图完整 URL」：admin 渲染上传控件（POST /api/upload 存 R2），
   * landing 把 logoUrl 当全屏背景铺满；此时 appName 语义为「按钮文字」。
   */
  bgUpload?: boolean;
}

export const TEMPLATES: readonly TemplateDescriptor[] = [
  {
    id: 'shield',
    label: 'Shield (安全校验页)',
    shortLabel: 'Shield',
    hasAppName: false,
    hasButtonColor: false,
    disabled: true,
  },
  {
    id: 'playstore',
    label: 'Play Store (仿应用商店)',
    shortLabel: 'Play Store',
    hasAppName: true,
    hasButtonColor: false,
    appNameHint: ' (Play Store 标题)',
    // 暂时停用：8s 挑战窗口导致大量真人被误拦，不再开放新建；现有链接仍可运行与解析
    disabled: true,
  },
  {
    id: 'download',
    label: 'Download (品牌引导页)',
    shortLabel: 'Download',
    hasAppName: true,
    hasButtonColor: true,
    defaultButtonColor: '#00BCD4',
  },
  {
    id: 'appstore',
    label: 'Loading 页',
    shortLabel: 'Loading 页',
    // 全程骨架伪装加载，弹窗 logo/名称也是占位 —— 不需要任何外观配置
    hasAppName: false,
    hasButtonColor: false,
  },
  {
    id: 'custom',
    label: 'Custom (自定义背景图)',
    shortLabel: 'Custom',
    // bgUpload 模板下字段语义复用：appName = 按钮文字，logo = 背景图 URL
    hasAppName: true,
    hasButtonColor: true,
    defaultButtonColor: '#1677FF',
    appNameHint: ' (按钮文字)',
    bgUpload: true,
  },
];

export function templateById(id: string): TemplateDescriptor | undefined {
  return TEMPLATES.find((t) => t.id === id);
}
